import { useState } from 'react';
import {
  Stack, Button, Chip, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Alert, Snackbar, Typography, Divider, Table, TableBody, TableRow, TableCell,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { repairMaintenanceApi, type RmComplaint } from '../../api/repairMaintenance.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const human = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '—');
const inr = (x?: number | null) => (x === null || x === undefined ? '—' : `₹${Number(x).toLocaleString('en-IN')}`);
const statusColor: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  open: 'warning', assigned: 'info', in_repair: 'info', resolved: 'success', closed: 'default', cancelled: 'error',
};

export function ComplaintsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['rm-complaints', page, limit], queryFn: () => repairMaintenanceApi.listComplaints({ page, limit }) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['rm-complaints'] }); qc.invalidateQueries({ queryKey: ['rm-complaint'] }); };

  const columns: Column<RmComplaint>[] = [
    { key: 'complaintCode', label: 'Complaint', width: 170 },
    { key: 'customerName', label: 'Customer' },
    { key: 'product', label: 'Product', render: (r) => r.product ?? '—' },
    { key: 'priority', label: 'Priority', render: (r) => <Chip size="small" label={human(r.priority)} /> },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" color={statusColor[r.status]} label={human(r.status)} /> },
    { key: 'warrantyStatus', label: 'Warranty', render: (r) => human(r.warrantyStatus) },
    { key: 'actions', label: '', width: 90, render: (r) => <Button size="small" onClick={() => setDetailId(r.id)}>Open</Button> },
  ];

  return (
    <>
      <PageHeader title="Complaints" subtitle="Repair & Maintenance — complaint intake, repair orders & warranty."
        action={hasPermission('rm_complaint.create') ? <Button startIcon={<Add />} variant="contained" onClick={() => setOpen(true)}>New Complaint</Button> : undefined} />
      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id} />

      {open && <NewComplaintDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); setToast('Complaint logged'); refresh(); }} />}
      {detailId && <ComplaintDetailDialog id={detailId} onClose={() => setDetailId(null)} onChanged={() => { setToast('Updated'); refresh(); }} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function NewComplaintDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ customerName: '', contact: '', product: '', serialNumber: '', description: '', channel: 'call', priority: 'medium', warrantyStatus: 'unknown' });
  const [error, setError] = useState<string | null>(null);
  const set = (k: string, v: string) => setForm({ ...form, [k]: v });
  const mut = useMutation({ mutationFn: () => repairMaintenanceApi.createComplaint({ ...form, contact: form.contact || undefined, product: form.product || undefined, serialNumber: form.serialNumber || undefined, description: form.description || undefined }), onSuccess: onDone, onError: (e) => setError(extractApiError(e)) });
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>New Complaint</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Customer Name" value={form.customerName} onChange={(e) => set('customerName', e.target.value)} fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label="Contact" value={form.contact} onChange={(e) => set('contact', e.target.value)} fullWidth />
            <TextField label="Product" value={form.product} onChange={(e) => set('product', e.target.value)} fullWidth />
          </Stack>
          <TextField label="Description" value={form.description} onChange={(e) => set('description', e.target.value)} fullWidth multiline rows={2} />
          <Stack direction="row" spacing={2}>
            <TextField select label="Channel" value={form.channel} onChange={(e) => set('channel', e.target.value)} fullWidth>
              {['call', 'email', 'portal', 'walk_in', 'field'].map((c) => <MenuItem key={c} value={c}>{human(c)}</MenuItem>)}
            </TextField>
            <TextField select label="Priority" value={form.priority} onChange={(e) => set('priority', e.target.value)} fullWidth>
              {['low', 'medium', 'high', 'critical'].map((c) => <MenuItem key={c} value={c}>{human(c)}</MenuItem>)}
            </TextField>
            <TextField select label="Warranty" value={form.warrantyStatus} onChange={(e) => set('warrantyStatus', e.target.value)} fullWidth>
              {['in_warranty', 'out_of_warranty', 'unknown'].map((c) => <MenuItem key={c} value={c}>{human(c)}</MenuItem>)}
            </TextField>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={form.customerName.length < 2 || mut.isPending} onClick={() => mut.mutate()}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}

function ComplaintDetailDialog({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [assignId, setAssignId] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [spare, setSpare] = useState({ description: '', quantity: '1', unitCost: '' });
  const [claimAmount, setClaimAmount] = useState('');
  const { data: c } = useQuery({ queryKey: ['rm-complaint', id], queryFn: () => repairMaintenanceApi.getComplaint(id) });
  const after = () => { qc.invalidateQueries({ queryKey: ['rm-complaint', id] }); onChanged(); };
  const onErr = (e: unknown) => setError(extractApiError(e));

  const assignMut = useMutation({ mutationFn: () => repairMaintenanceApi.assignComplaint(id, { assigneeType: 'technician', assigneeId: Number(assignId) }), onSuccess: after, onError: onErr });
  const closeMut = useMutation({ mutationFn: () => repairMaintenanceApi.closeComplaint(id), onSuccess: after, onError: onErr });
  const repairMut = useMutation({ mutationFn: () => repairMaintenanceApi.createRepair({ complaintId: id, diagnosis: diagnosis || undefined }), onSuccess: () => { setDiagnosis(''); after(); }, onError: onErr });
  const warrantyMut = useMutation({ mutationFn: () => repairMaintenanceApi.createWarranty({ complaintId: id, claimAmount: Number(claimAmount) }), onSuccess: () => { setClaimAmount(''); after(); }, onError: onErr });

  const activeRepair = (c?.repairOrders ?? []).find((r) => r.status === 'in_progress');
  const spareMut = useMutation({ mutationFn: () => repairMaintenanceApi.addSpare(activeRepair!.id, { description: spare.description, quantity: Number(spare.quantity), unitCost: spare.unitCost ? Number(spare.unitCost) : undefined }), onSuccess: () => { setSpare({ description: '', quantity: '1', unitCost: '' }); after(); }, onError: onErr });
  const completeMut = useMutation({ mutationFn: () => repairMaintenanceApi.completeRepair(activeRepair!.id, {}), onSuccess: after, onError: onErr });
  const decideMut = useMutation({ mutationFn: ({ claimId, decision, amount }: { claimId: number; decision: string; amount?: number }) => repairMaintenanceApi.decideWarranty(claimId, { decision, settledAmount: amount }), onSuccess: after, onError: onErr });

  if (!c) return null;
  const canRepair = hasPermission('rm_repair.create') && !['closed', 'cancelled'].includes(c.status);

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle><Stack direction="row" spacing={1.5} alignItems="center"><span>{c.complaintCode}</span><Chip size="small" color={statusColor[c.status]} label={human(c.status)} /><Chip size="small" label={human(c.priority)} /></Stack></DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="subtitle1">{c.customerName}</Typography>
        <Typography variant="body2" color="text.secondary">{c.product ?? '—'} · {c.serialNumber ?? '—'} · {human(c.warrantyStatus)}</Typography>
        {c.description && <Typography variant="body2" mt={1}>{c.description}</Typography>}

        {c.status === 'open' && hasPermission('rm_complaint.assign') && (
          <Stack direction="row" spacing={1} mt={2} alignItems="center">
            <TextField size="small" label="Technician ID" type="number" value={assignId} onChange={(e) => setAssignId(e.target.value)} />
            <Button size="small" variant="contained" disabled={!assignId} onClick={() => assignMut.mutate()}>Assign</Button>
          </Stack>
        )}

        <Divider textAlign="left" sx={{ my: 1.5 }}>Repair Orders</Divider>
        <Table size="small"><TableBody>
          {(c.repairOrders ?? []).map((r) => (
            <TableRow key={r.id}><TableCell>{r.repairCode}</TableCell><TableCell><Chip size="small" label={human(r.status)} /></TableCell><TableCell>{r.diagnosis ?? '—'}</TableCell><TableCell align="right">{inr(r.totalCost)}</TableCell></TableRow>
          ))}
          {(c.repairOrders ?? []).length === 0 && <TableRow><TableCell colSpan={4}>No repair orders.</TableCell></TableRow>}
        </TableBody></Table>
        {canRepair && !activeRepair && (
          <Stack direction="row" spacing={1} mt={1} alignItems="center">
            <TextField size="small" label="Diagnosis" value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} fullWidth />
            <Button size="small" variant="contained" onClick={() => repairMut.mutate()}>Start Repair</Button>
          </Stack>
        )}
        {activeRepair && hasPermission('rm_repair.update') && (
          <Stack spacing={1} mt={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField size="small" label="Spare" value={spare.description} onChange={(e) => setSpare({ ...spare, description: e.target.value })} />
              <TextField size="small" label="Qty" type="number" value={spare.quantity} onChange={(e) => setSpare({ ...spare, quantity: e.target.value })} sx={{ width: 80 }} />
              <TextField size="small" label="Unit Cost" type="number" value={spare.unitCost} onChange={(e) => setSpare({ ...spare, unitCost: e.target.value })} sx={{ width: 110 }} />
              <Button size="small" disabled={!spare.description} onClick={() => spareMut.mutate()}>Add Spare</Button>
              {hasPermission('rm_repair.complete') && <Button size="small" color="success" variant="contained" onClick={() => completeMut.mutate()}>Complete</Button>}
            </Stack>
          </Stack>
        )}

        <Divider textAlign="left" sx={{ my: 1.5 }}>Warranty Claims</Divider>
        <Table size="small"><TableBody>
          {(c.warrantyClaims ?? []).map((w) => (
            <TableRow key={w.id}>
              <TableCell>{w.claimCode}</TableCell>
              <TableCell><Chip size="small" label={human(w.status)} /></TableCell>
              <TableCell align="right">{inr(w.claimAmount)}</TableCell>
              <TableCell align="right">
                {hasPermission('rm_warranty.decide') && w.status === 'raised' && <Button size="small" onClick={() => decideMut.mutate({ claimId: w.id, decision: 'approved' })}>Approve</Button>}
                {hasPermission('rm_warranty.decide') && w.status === 'approved' && <Button size="small" onClick={() => decideMut.mutate({ claimId: w.id, decision: 'settled', amount: w.claimAmount })}>Settle</Button>}
              </TableCell>
            </TableRow>
          ))}
          {(c.warrantyClaims ?? []).length === 0 && <TableRow><TableCell colSpan={4}>No claims.</TableCell></TableRow>}
        </TableBody></Table>
        {hasPermission('rm_warranty.create') && c.warrantyStatus === 'in_warranty' && (
          <Stack direction="row" spacing={1} mt={1} alignItems="center">
            <TextField size="small" label="Claim Amount" type="number" value={claimAmount} onChange={(e) => setClaimAmount(e.target.value)} />
            <Button size="small" variant="outlined" disabled={!claimAmount} onClick={() => warrantyMut.mutate()}>Raise Claim</Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {hasPermission('rm_complaint.close') && !['closed', 'cancelled'].includes(c.status) && <Button onClick={() => closeMut.mutate()}>Close Complaint</Button>}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
